import { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowRight, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { LoadingSpinner } from '../Layout';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATES = ['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA',
  'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC',
  'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'];

/** The basics, inside the welcome instead of on a separate page before it. */
export const BasicsStep = ({ accent = '#22d3ee', onDone }) => {
  const { user, getAuthHeader, refreshUser } = useAuth();
  const [form, setForm] = useState({ full_name: '', date_of_birth: '', zip_code: '', city: '', state: '' });
  const [saving, setSaving] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    axios.get(`${API}/profile/basics`, getAuthHeader())
      .then(r => setForm(f => ({ ...f, ...Object.fromEntries(
        Object.entries(r.data.basics || {}).filter(([, v]) => v)) })))
      .catch(() => {})
      .finally(() => setReady(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setZip = async (zip) => {
    setForm(f => ({ ...f, zip_code: zip }));
    if (String(zip).length === 5) {
      try {
        const r = await axios.get(`${API}/location/lookup/${zip}`);
        if (r.data?.city) setForm(f => ({ ...f, city: r.data.city, state: r.data.state || f.state }));
      } catch { /* they can type it themselves */ }
    }
  };

  const age = form.date_of_birth
    ? Math.floor((Date.now() - new Date(form.date_of_birth).getTime()) / 31557600000) : null;

  const save = async () => {
    if (!form.full_name || form.full_name.trim().length < 2) return toast.error('What should we call you?');
    if (!form.date_of_birth) return toast.error('Your date of birth sets what is open to you');
    if (!form.state) return toast.error('Pick your state, wages and costs depend on it');
    setSaving(true);
    try {
      await axios.post(`${API}/profile/basics`, form, getAuthHeader());
      if (refreshUser) refreshUser();
      onDone(age);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save that just now');
    } finally {
      setSaving(false);
    }
  };

  if (!ready) return <div className="py-24 flex justify-center"><LoadingSpinner /></div>;

  return (
    <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
      className="max-w-xl mx-auto" data-testid="welcome-basics">
      <p className="text-[10px] uppercase tracking-[0.22em] text-slate-300 text-center">First, the basics</p>
      <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-white text-center tracking-tight mt-3">
        Let's get you on the map.
      </h2>
      <p className="text-sm text-slate-300 text-center mt-3 max-w-md mx-auto">
        Four things, once. Your name, your age and where you live decide which options are actually
        open to you and what they pay.
      </p>

      <div className="mt-8 space-y-5">
        <div className="space-y-2">
          <Label className="text-slate-300 text-sm">What should we call you?</Label>
          <Input value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))}
            placeholder={user?.full_name || 'Your name'} className="input-dark" data-testid="basics-name" />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-slate-300 text-sm">Date of birth</Label>
            <Input type="date" value={form.date_of_birth}
              onChange={e => setForm(f => ({ ...f, date_of_birth: e.target.value }))}
              className="input-dark" data-testid="basics-dob" />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300 text-sm">ZIP code</Label>
            <Input value={form.zip_code} maxLength={5} onChange={e => setZip(e.target.value)}
              placeholder="Fills in your city" className="input-dark" data-testid="basics-zip" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-slate-300 text-sm">City</Label>
            <Input value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
              placeholder="City" className="input-dark" data-testid="basics-city" />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300 text-sm">State</Label>
            <Select value={form.state} onValueChange={v => setForm(f => ({ ...f, state: v }))}>
              <SelectTrigger className="input-dark" data-testid="basics-state">
                <SelectValue placeholder="Select state" />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                {STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {age !== null && age >= 0 && age < 120 && (
          <p className="text-xs text-slate-300" data-testid="basics-age-echo">
            That makes you {age}. We'll use it to keep every suggestion legal and realistic for you.
          </p>
        )}
      </div>

      <Button onClick={save} disabled={saving} data-testid="basics-continue"
        className="w-full mt-8 rounded-full text-slate-900 font-semibold py-6"
        style={{ background: `linear-gradient(90deg, ${accent}, #a78bfa)` }}>
        {saving ? <LoadingSpinner size="sm" /> : <>Continue <ArrowRight className="w-4 h-4 ml-1.5" /></>}
      </Button>
      <p className="text-[11px] text-slate-300 text-center mt-4 flex items-center justify-center gap-1.5">
        <Check className="w-3 h-3" /> Private to your account. You can change any of it later.
      </p>
    </motion.div>
  );
};

export default BasicsStep;
